import { ConrepAppFilterPreset, Vector3 } from 'src/Conrep/App/types';
import { FileVersionView, FileVersionViewReference } from 'src/file/types';

export enum KeyTypes {
  _3D = '3d',
  _2D = '2d',
  OTHER = 'other',
}

export type GroupedViewerModelOptions = {
  [k in KeyTypes]: FileVersionView[];
};

export type ForgeViewerNaming = {
  [dbId: number]: string;
};

export type ForgeViewerDbIdToExternalIdMapping = {
  [id: number]: string;
};

export type ForgeViewerExternalIdToDbIdMapping = {
  [externalId: string]: number;
};

export type ForgeViewerExternalIdToFileIdMapping = {
  [externalId: string]: string;
};

export enum ForgeViewerHeaderType {
  SHEETSET,
  FILE,
  VIEW,
}

export type ForgeToken = {
  forgeToken: {
    credentials: {
      access_token: string;
      expires_in: number;
    };
  };
};

type ForgeDiagnosticsProbeResult = {
  ok: boolean;
  statusCode?: number;
  statusMessage?: string;
  body?: unknown;
  error?: string;
};

type ForgeRegionAlignmentStatus =
  | 'aligned'
  | 'wrong_region'
  | 'missing'
  | 'in_progress'
  | 'unknown';

export type ForgeRegionAlignment = {
  status: ForgeRegionAlignmentStatus;
  configuredRegion: 'EMEA';
  manifestRegion?: string;
  message: string;
};

export type ForgeAccountDiagnostics = {
  forgeAppConfigured: boolean;
  forgeBucketName: string;
  configuredDerivativeRegion: 'EMEA';
  viewerStreamingApi: 'streamingV2_EU';
  authentication: {
    readToken: ForgeDiagnosticsProbeResult;
    writeToken: ForgeDiagnosticsProbeResult;
  };
  bucket: {
    name: string;
    accessible: boolean;
    statusCode?: number;
    policyKey?: string;
    error?: string;
  };
  modelDerivativeFormats: {
    eu: ForgeDiagnosticsProbeResult;
    us: ForgeDiagnosticsProbeResult;
  };
  manifest: {
    configured: ForgeDiagnosticsProbeResult;
    opposite: ForgeDiagnosticsProbeResult;
    eu: ForgeDiagnosticsProbeResult;
    us: ForgeDiagnosticsProbeResult;
  } | null;
  regionAlignment: ForgeRegionAlignment | null;
  analysis: {
    summary: string;
    likelyCauses: string[];
  };
};

export type ForgeRegionMismatchEntry = {
  projectId: string;
  fileId: string;
  fileName: string;
  fileType: string;
  versionId: string;
  viewerUrnEncoded: string;
  regionAlignment: ForgeRegionAlignment;
  manifest: {
    configured: ForgeDiagnosticsProbeResult;
    opposite: ForgeDiagnosticsProbeResult;
  };
};

export type ForgeRegionMismatchScan = {
  configuredDerivativeRegion: 'EMEA';
  viewerStreamingApi: 'streamingV2_EU';
  scannedCount: number;
  mismatchCount: number;
  mismatches: ForgeRegionMismatchEntry[];
};

export type ForgeView = {
  guid: string;
  name: string;
  viewableID: string;
  role?: string;
  size?: number;
};

export type RestoreViewData = {
  viewReference: FileVersionViewReference;
  eye: Vector3;
  up: Vector3;
  target: Vector3;
  filterPreset: ConrepAppFilterPreset;
  markupUrl?: string;
};

export type ForgeElementProperties = Record<string, Record<string, string>>;

export type ForgeElementPropertiesSet = Record<string, string[]>;
